<template>
  <v-timeline
    id="roadmap"
    class="mb-12"
    dense
  >
    <template v-for="(item, i) in items">
      <v-timeline-item
        v-if="typeof item === 'number'"
        :key="`divider-${i}`"
      >
        <template v-slot:opposite>
          <span>Completed</span>
        </template>
      </v-timeline-item>
      <v-timeline-item
        v-else-if="index == null || index >= i"
        :key="i"
        :icon="item.complete ? 'done' : item.icon"
        :color="item.complete ? 'success darken-1' : item.color"
        :small="item.complete || item.small"
        fill-dot
      >
        <template v-slot:opposite>
          <strong
            :class="index === i ? 'primary--text' : undefined"
            v-text="item.caption"
          />
        </template>
        <v-card
          :class="`elevation-${item.value ? 8 : 1}`"
          :hover="true"
          class="py-2"
          @click.native="item.value = !item.value"
        >
          <v-card-title class="py-0 pr-2">
            <Markdown
              class="body-2"
              :code="item.title"
            />
            <v-spacer/>
            <v-btn
              :color="item.value ? 'primary' : ''"
              :disabled="item.complete"
              :input-value="item.value"
              :ripple="false"
              class="font-weight-light ma-0 v-btn--flat v-btn--text"
              text
            >
              <span
                class="mr-2"
                v-text="item.caption"
              />
              <v-icon small>
                date_range
              </v-icon>
            </v-btn>
          </v-card-title>
          <v-expand-transition  @enter="sendPanelClickDataToGA(item.title)">
            <div v-if="(index != null && index >= i) || item.value">
              <v-card-text>
                <Markdown v-if="(item.text)" :code="item.text"/>
                <template v-if="item.features">
                  <template v-for="(feature, index) in item.features">
                    <Markdown v-if="(feature.title)" :key="`title-${index}`" :code="feature.title"/>
                    <Markdown v-if="(feature.description)" :key="`description-${index}`" :code="feature.description"/>
                    <Markdown v-if="(feature.items)" :key="`list-${index}`" :code="feature.items"/>
                  </template>
                </template>
              </v-card-text>
            </div>
          </v-expand-transition>
        </v-card>
      </v-timeline-item>
    </template>
  </v-timeline>
</template>

<script>
  import Markdown from "./Markdown";

  export default {
    name: 'Roadmap',
    components: {
      Markdown
    },
    data() {
      return {
        index: null,
        interval: null,
        items: [],
      }
    },
    methods: {
      sendPanelClickDataToGA(itemText) {
        console.log('Roadmap:'+ itemText);
        this.$ga.event({
          eventCategory: 'Roadmap',
          eventAction: 'panel-click',
          eventLabel: `panel: ${itemText}`
        });
      }
    },
    mounted() {
      const itemData = [
        {
          "type": "release",
          "complete": true,
          "title": "Release 2.2",
          "caption": "August 2019",
          "text": "",
          "features": [
            {
              "title": "#### AMU Delegation – Initial Scope",
              "description": "The ability for Principals to delegate access to ICT PLUS is an important feature request we aim to provide. In this release, the following initial scope will be available:",
              "items": [
                "A principal may delegate any number of non-principal staff members access to ICT PLUS. To do this, select \"ICP - ICT PLUS Delegation\" from the applications list in AMU.",
                "If a staff member is given access to multiple schools (e.g. If two principals delegate to one person), only one delegation is considered (the staff member will only see one school of the two).",
                "If a principal delegates access to another principal, the principal will only see their own school, not the one that they were delegated access to.",
              ],
            }
          ],
          "value": false
        },
        {
          "type": "release",
          "complete": true,
          "title": "Release 2.3",
          "caption": "September 2019",
          "text": "",
          "features": [
            {
              "title": "#### Access to Relieving Principals",
              "description": "Relieving Principals can now request access, for themselves and staff at the school, by [logging a ticket](https://education.nsw.gov.au/inside-the-department/edconnect/about-us/contact-us) with ED Connect.",
            },
            {
              "title": "#### AMU Delegation – Remaining Scope",
              "description": "In Release 2.2, a limited scope of AMU delegation was available (see in *“13th August 2019 – Release 2.2”  above).*  In this release, we have enabled a school-picker for users who have been delegated access to multiple schools’ ICT PLUS sites."
            },
            {
              "title": "#### User Feedback Enhancements - Data Fixes",
              "description": "You’ve provided feedback flagging a few issues with the data coming through from source systems. We have made it a priority to address these in time for the T4L equipment Roll-out and have pushed the Internet Usage page to Release 2.5 (October) as a result. In this September 6th release, we are delivering the below fixes:<br><br>" +
                "*Warranty data* | 5% of the fleet of managed devices in schools do not have any warranty data in Remedy. Because of this, the devices were showing up in ICT PLUS as having expired warranty, which is incorrect. In this release, those devices will appear as \"Warranty Data Missing\" in the **Warranty Chart** and **Devices List**.<br><br>" +
                "*Age of Device -> Age of Model* | It has been identified that the **Age of Device** chart/column actually sources the Release Date of the model of a device, not the purchase date of the device. We are renaming the “Age of Device” chart and column to “Age of Model” to reflect this, and have added to our backlog the ability to capture “Age of Device”."
            }
          ],
          "value": false
        },
        {
          "type": "release",
          "complete": true,
          "title": "Release 2.3.4 (Spot Release)",
          "caption": "September 2019",
          "text": "",
          "features": [
            {
              "title": "#### User Feedback Enhancements - Data Fixes",
              "description": "You’ve provided feedback flagging a few issues with the data coming through from source systems. We have made it a priority to address these in time for the T4L equipment Roll-out and have pushed the Internet Usage page to Release 2.6 (November) as a result. In this spot-release, we are delivering the below fix:<br><br>" +
                "*Device Usage* | The **Usage** chart/column was previously based on *last_logon_time* pulled in via SCCM; what we have found is that this field is not always populated. We've found another time-based field in SCCM which seems to be consistent for an *\"actively\"* used device. This is supported for Microsoft devices only. We are working on a way to get usage statistics for Google and Apple devices, and have scheduled this for the November release."
            }
          ],
          "value": false
        },
        {
          "type": "release",
          "complete": true,
          "title": "Release 2.4",
          "caption": "October 2019",
          "text": "",
          "features": [
            {
              "title": "#### The Logout Bug - Fixed",
              "description": "*Symptom* | Users reported opening ICT PLUS and seeing “You don't have a school selected, so we can't display anything yet” with the pages not showing any data, and with no toolbar to allow them to log out or use a school picker if they usually have one in the ICT PLUS.<br>" +
                "*Reason* | This happened when users hadn’t logged out for an extended period without clicking \"Logout\", and we shared a temporary workaround for this in our [Yammer group](https://www.yammer.com/det.nsw.edu.au/#/threads/inGroup?type=in_group&feedId=8610013184&view=all).<br>" +
                "*Solution* | The team has developed an auto-logout to fix this bug and adhere to the department's session timeout standards. Now, when you close your ICT PLUS tab or browser window, you will be logged out, and if you have been logged in for 40 minutes you will receive a pop-up window prompting you to logout or extend the session.",
            },
            {
              "title": "#### Our Roadmap",
              "description": "This Roadmap page has a new look to help you more easily identify what's already been released and what's scheduled for upcoming releases."
            }
          ],
          "value": false
        },
        {
          "type": "release",
          "complete": true,
          "title": "Release 2.5",
          "caption": "November 2019",
          "text": "",
          "features": [
            {
              "title": "#### Planning Dashboard Enhancements",
              "description": "*The “Devices (Managed Only)” page* | The Devices List previously appeared at the bottom of the Planning Dashboard as well as a separate page in the left sidebar menu, to allow users to view larger lists of devices on the screen. For simplicity’s sake, that page has now been removed and combined with the Planning Dashboard Page, as a tab. When you visit the Planning Dashboard, you can now toggle between Charts and Devices by clicking on the tab names, without losing your filters.<br><br>" +
                "*Filtering by Multiple Parameters* | Previously, when looking at the charts in the Planning Dashboard, you could click on the segment of a single chart to filter all of the remaining charts by that segment. Now, you can additionally click on segments from multiple charts to be able to filter by more than one parameter. For example, you could click on the “Laptop” Form Factor and the “Data not available” Warranty and to see all laptops with no warranty data available. You can easily clear filters by clicking on the respective “x” alongside the filter poo-ups that appear above the tab names. <br><br>"+
                "*Build Number for Devices* | Based on popular demand, we have added a “Build Number” column to the Devices List, to aid you in identifying devices in your school with older builds that need to be upgraded.<br><br>"+
                "*Download CSV* |On user request, when you click the download icon in the Devices List, you will now be downloading the filtered data if you have any filters applied. If you would like all data to be downloaded, simply “clear” all of your filters before downloading a csv. As a reminder of this, when you hover on the download icon you will see the text “Download current data as CSV” appear. <br><br>"+
                "*Look and Feel Changes* | The Planning Dashboard now has the school details appearing at the top of the page, with the option to expand the School Name tile to reveal the Location, Operational Directorate, and Principal Network of the school.",
            },
            {
              "title": "#### Bringing Field Operations On Board",
              "description": "We have provided preliminary access to the back end of ICT PLUS data to our focus group of Field Operations staff. With their valuable analysis and experience, we aim to work together to find out how we can provide further insight and value to schools through ICT PLUS."
            }
          ],
          "value": false
        },
        {
          "type": "release",
          "complete": true,
          "title": "Release 2.6",
          "caption": "February 2020",
          "text": "",
          "features": [
            {
              "title": "#### User Feedback Enhancements",
              "description": "Based on feedback tickets submitted by our users, we have made the changes below:<br><br>" +
                "*Duplicate Devices* | It has been spotted that some data sets have duplicate devices. This release has made an improvement in removing duplicate devices. Though, we haven’t resolved this completely, so if you see any more duplicates records of devices in your school please let us know by submitting a Feedback ticket.<br><br>" +
                "*Decommissioned Devices* | Previously, decommissioned devices weren’t being removed from the ICT PLUS database; this has now been resolved.<br><br>" +
                "*Chrome Devices* | Chrome data in ICT PLUS is now back online and being updated regularly, every 3 hours from midnight. If your recently purchased Chrome devices weren’t appearing in ICT PLUS, please have a look as they should be in the database now.<br><br>" +
                "*Form Factor Bug* | Previously, MacBook Pros and Airs were incorrectly categorised as Desktops in the Form Factor column/chart of the Planning Dashboard. This has been fixed, these devices now appear as Laptops.<br><br>" +
                "*Last Seen Chart* | In the Planning Dashboard, the Device Usage chart/column has been renamed to “Last Seen”, as the previous title could be easily misunderstood. This chart, and column, is based on the time that the device last checked in to the DoE network."
            },
            {
              "title": "#### User Experience Enhancements",
              "description": "We are continuing work on updating ICT PLUS to make your experience more user friendly.<br><br>"+
                "*Device List Column Freeze* | In the Planning Dashboard, the Device List now has the “Device Name” column frozen, so that no matter how far to the right you scroll across the columns you have the visibility to easily match up devices with the details you are viewing.<br><br>" +
                "*Page Headers* | The Network Usage and Roadmap pages in ICT PLUS now have a page header, with an information symbol you can click for a brief explanation of the page.<br><br>" +
                "*Global Filters* | In the Planning Dashboard, you can now filter device information across the Charts and Devices tab by multiple parameters using drop down menus, rather than clicking on graph segments, for a friendlier user experience.<br><br>" +
                "*Devices Per Student* | This tile, in the top right of the Planning Dashboard, has now been renamed to *Managed devices per FTE student*. We know, it’s a mouthful, but the previous wording was misleading. This tile is simply a division of the number of managed devices by the number of FTE students. However, at the moment we are not able to differentiate student devices from staff devices; we are aware the figure in this tile is still not an accurate depiction but at this stage it is intended to provide a ballpark figure.<br><br>" +
                "*Chart Legends* | In the Charts tab of the Planning Dashboard, each chart’s legend now shows the number of devices per chart segment; the number of devices appears in brackets, next to the corresponding label.<br><br>" +
                "*Changing ICT PLUS+ to ICT PLUS* | Confused by the extra “+” symbol in the tool name? It didn’t make much sense to us either, so we’ve dropped it."
            },
          ],
          "value": false
        },
        {
          "type": "release",
          "complete": true,
          "title": "Release 2.7",
          "caption": "March 2020",
          "text": "",
          "features": [
            {
              "title": "#### User Feedback Enhancements",
              "description": "*New Charts* | By popular demand, new charts have been added to the Planning Dashboard to display the *Top 10 Build Numbers* and *Top 10 Operating Systems* in your school’s managed devices. These have also been added as global filters in the Planning Dashboard, to allow you to view a more targeted subset of devices in the Charts and Devices tabs.<br><br>" +
                "*Device List Columns* | You now have the ability to select which columns you’d like to display in the Devices tab of the Planning Dashboard. Simply click the settings cog, and choose the columns you’d prefer to show.<br><br>" +
                "Note, if you have filtered your Devices list and then download an export, the export will contain only the rows (devices) you have filtered, but will show all of the columns available in the Devices tab in ICT PLUS. We aim to fix this in Release 2.8, so that the exports only contain the rows *and columns* that you have filtered in ICT PLUS.<br><br>" +
                "Your column preferences, to show/hide particular columns, are saved to your device’s local storage. This means that, on each device you use to log in to ICT PLUS, your column preferences will be saved even after you navigate away from the Devices page, switch to another school, log out and log back in, or close your browser window/session and open ICT PLUS again. Check out our new FAQ about column preferences for more detail."
            },
            {
              "title": "#### User Experience Enhancements",
              "description": "*Error Handling* | We have enabled error pop-ups in the event that there is an issue on the backend of ICT PLUS. This pop-up will flag to you that there is an issue, and allow you to report it to us."
            },
          ],
          "value": false
        },
        {
          "type": "release",
          "complete": true,
          "title": "Release 2.8",
          "caption": "May 2020",
          "text": "",
          "features": [
            {
              "title": "#### User Feedback Enhancements",
              "description": "*Device Location* | For Microsoft managed devices we now display the B/L/R location that you manually input into UDM for each device. If you have not populated this field in UDM, the value for that device’s location will be “Data Not Available”. In the first instance, this data will be updated once a week in ICT PLUS, check in on Fridays for new data.<br><br>" +
                "We are looking at alternative automated ways to get a hold of location data in the long term, without the need for you to manually insert the location of a device into UDM first and which also provides location data for Chrome and Apple managed devices. Having the data provided in this “live” nature would also help you determine when a device has moved locations in your school without your knowledge.<br><br>" +
                "*Age of Model* | The data in the Age of Model column has been converted to dates rather than the date ranges (such as “Almost 3 years old”) displayed in the Age of Model chart. This column has now been more adequately renamed to “Model Release Date”, and remains the source of data for the Age of Model chart.<br/><br/>"+
                "*Historical Network Usage* | In the Network Usage page you have the ability to query data up to 3 months prior, via a date picker, for your WAN and Internet@Edge Utilisation graphs. The date picker will allow you to select up to 7 days at a time, due to the immensity of the data being processed.<br/><br/>"+
                "*Bandwidth Cap* | In the Network Usage page, we now display the bandwidth cap for your WAN & Internet@Edge utilisation graphs. This is displayed above the top right of each graph."
            },
            {
              "title": "#### User Experience Enhancements",
              "description": "*Top 10 Model Global filter* | A “Model” filter has been added to the Planning Dashboard, to allow you to view a more targeted subset of devices in the Charts and Devices tabs. The options in this filter come from your “Top 10 Model count” chart.<br><br>" +
                "*CSV Download* | We have now enhanced the CSV download function in the Devices table. If you have filtered your Devices and download a CSV, the export will only contain the rows and columns that you have filtered in the Devices tab in ICT PLUS.<br><br>" +
                "*Devices Table Date Filter* | The Devices table now has a date filter, located alongside the search bar. This filter will allow you to find a more targeted subset of your devices, for columns that have a date value. First, use the drop-down to select the column you would like to filter, then use the date picker to select your date range. For example, you can use this to find devices with warranty due to expire in the next 6 weeks by using this filter to select the “Warranty Expires” from the drop-down menu, and using the date picker to select the “from” and “to” dates you are interested in.<br><br>" +
                "*Devices Table Header & Footer Freeze* | In the Planning Dashboard Devices tab, we know that when you are looking at a lot of devices it is frustrating to have to scroll all the way to the top of the page to identify the headings of the columns you’re looking at, and then also scroll all the way to the bottom of the page to scroll horizontally if you don’t have a trackpad. We have resolved this by making the Devices table have a fixed height, so that the header with the column names and the footer with the horizontal scroll bar are always visible as you scroll across the available rows and columns of the Devices table. The rows per page drop-down has also been updated to show higher numbers. Previously, you could choose to show *5, 10, 25,* or *All* devices at a time, which were all too similar. Now, you can show *10, 25, 50, 100,* or *All* devices at time.<br><br>" +
                "*Devices Table Column Width* | We have now removed unnecessary column padding in the Planning Dashboard Devices table, so that columns don’t take up more valuable screen space than they need to."
            },
          ],
          "value": false
        },
        {
          "type": "release",
          "complete": true,
          "title": "Release 2.9",
          "caption": "June 2020",
          "text": "",
          "features": [
            {
              "title": "#### User Experience Enhancements",
              "description": "*Home Page* | We’ve slightly changed the ICT PLUS sitemap – when you log in you’ll now see a home page rather than the Planning Dashboard, to give you some high-level context of ICT PLUS, which will be especially useful to any new users. The figures for the count of Managed Devices and FTE Students (as of the August census) now appear on this page rather than the Planning Dashboard page.<br/><br/>" +
                "In addition, the Planning Dashboard page has been renamed to “Managed Devices” for clarity’s sake, as we’re aware the previous name was ambiguous. You’ll note this page now has a header, with a description when you click the “i” icon, and the ratio of devices to students has been removed as it was largely an approximation. We will aim to return this figure to ICT PLUS when the data becomes available to make an accurate figure.<br/><br/>" +
                "*Help & Support Page* | The FAQ page has been renamed to the “Help & Support” page to give you a greater understanding of what ICT PLUS provides and allow you to determine how best to use it. In addition to the existing “FAQ” tab, this page now has a “Data Sources” tab showing where the data comes from and how often it is updated, a “Data Glossary” tab to describe the data we provide, and a “User Guides” tab linking out to QRG’s for new users.<br/><br/>" +
                "*Multi-sited Schools* | Previously, schools with multiple sites had no visibility over the Network Usage in each of their sites. The Network Usage page now has multiple tabs to show the relevant graphs for each site – you’ll see a tab for “Main Site”, “Sub Site 1”, “Sub Site 2”, depending on how many sites you have in your school.<br/><br/>" +
                "*Column Renamed* | In the Devices table of the Managed Devices page, we’ve renamed the “Specifications” column to “CPU Spec”, for clarity."
            },
            {
              "title": "**User Feedback Enhancements**",
              "description": "School Picker | As requested from our field operations users, if you have access to multiple schools in ICT PLUS, you now have the ability to search for schools by their site code (school code)."
            }
          ],
          "value": false
        },
        {
          "type": "release",
          "complete": true,
          "title": "Release 2.10",
          "caption": "June 2021",
          "text": "",
          "features": [
            {
              "title":"",
              "description": "**Data Enhancements**<br/><br/>" +
                "In the Managed Devices page, we have made enhancements to the data sources and update intervals as noted below. For more information, visit the Data Sources and Data Glossary tabs of the Help and Support page.<br/><br/>" +
                "*B/L/R Location Data* | ICT PLUS previously pulled this data from the source manually on a weekly basis. In this release we’ve automated this, B/L/R Location will now be updated daily at 1am. B/L/R Location data remains information that is only available for Microsoft Managed Devices, as it is populated in UDM.<br/><br/>" +
                "*Apple Data* | ICT PLUS previously used Munki Reports, now a legacy system, as the data source for Apple managed devices. Jamf has since been made available for consumption and with this release Jamf is now the data source for Apple managed devices. This is quite beneficial as Jamf provides data that is more regularly updated and contains data for managed iOS devices such as iPhones and iPads. With this change, Warranty Expiry data is no longer available for Apple managed devices, however this has been added to the Product Backlog for future rectification.<br/><br/>" +
                "**User Feedback Enhancements**<br/><br/>" +
                "*Memory Bug* | In the Memory column of the Managed Devices table, a recent bug caused the figures to be suffixed with Mb rather than GB. This has now been fixed.<br/><br/>" +
                '*FAQ Question* | Additional information has been added to Question 6 of the FAQ page “6. Why does the Managed Devices page only show some, not all, of my devices?”.'
            }

          ],
          "value": false
        },
        {
          "type": "release",
          "complete": false,
          "title": "Release 2.11",
          "caption": "TBD",
          "text": "",
          "features": [
            {
              "title":"",
              "description": "ICT PLUS is currently in maintenance mode (i.e. keep it running, fix bugs) due to funding constraints. This means new features, unfortunately, aren't currently possible however we are addressing bugs as they come through.<br/><br/>" +
                "We’ve noted the feedback tickets that have been coming through from our users requesting new features and reporting minor bugs, thank you for sending those through.<br/><br/>" +
                "Please do continue to provide feedback as it provides valuable visibility which will help us secure resources to deploy new features."
            }

          ],
          "value": false
        }
      ];

      this.items = itemData.map(item => {
        const type = types[item.type]
        return {
          ...type,
          ...item,
        }
      }).reverse()
    },
  };

  const types = {
    release: {
      icon: 'event',
      color: 'brand-color',
    }
  }
</script>

<style scoped>
  #roadmap >>> div.success.darken-1 {
    background-color: #69B32D !important;
    border-color: #69B32D !important;
  }

  #roadmap >>> div.brand-color {
    background-color: #407EC9 !important;
    border-color: #407EC9 !important;
  }
</style>
